/**
 * InstallPrompt — invites the user to add YUO to their home screen.
 *
 * • Chrome/Edge/Android: listens for `beforeinstallprompt`, then shows a soft
 *   glow-capsule "Install YUO" pill at the bottom-center. Tapping fires the
 *   native mini-infobar.
 * • iOS Safari (no beforeinstallprompt): shows a one-liner with the
 *   Share → "Add to Home Screen" instruction.
 * • Once installed (matchMedia: display-mode: standalone) or dismissed for a
 *   week (localStorage), the prompt stays hidden.
 */
import { useEffect, useState, useCallback } from "react";
import { INK, MUTED_60, FONT as YFONT } from "@/lib/yuoDesign";
import StoneOrb from "@/components/yuo/StoneOrb";

const DISMISS_KEY = "yuo_pwa_dismissed_at";
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000; // 1 week

function isStandalone() {
  return (
    (typeof window !== "undefined" &&
      window.matchMedia?.("(display-mode: standalone)")?.matches) ||
    // iOS
    (typeof navigator !== "undefined" && navigator.standalone === true)
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // Classic iPhone / iPod detection
  if (/iPhone|iPod/.test(ua) && !window.MSStream) return true;
  // iPadOS 13+ hides "iPad" in the UA and reports as Mac.
  // The reliable tell: Mac-like UA + touch support (>1 touch point).
  const isMacLike = /Macintosh|Mac OS X/.test(ua);
  const isTouch = (navigator.maxTouchPoints || 0) > 1;
  if (isMacLike && isTouch) return true;
  // Legacy "iPad" tag (older iPadOS versions).
  if (/iPad/.test(ua)) return true;
  return false;
}

function wasDismissedRecently() {
  try {
    const at = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    return at && Date.now() - at < DISMISS_MS;
  } catch { return false; }
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showIOS, setShowIOS] = useState(false);
  const [hidden, setHidden] = useState(true);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasDismissedRecently()) return;

    // Native (Android / Chrome / Edge / Samsung Internet)
    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
      setHidden(false);
    };
    window.addEventListener("beforeinstallprompt", onBefore);

    // iOS / iPadOS: no native event — show the tappable "How to install"
    // capsule after a short delay so the user finishes reading first.
    let iosTimer = null;
    if (isIOS()) {
      iosTimer = setTimeout(() => { setShowIOS(true); setHidden(false); }, 3500);
    }

    const onInstalled = () => {
      setHidden(true);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setHidden(true);
    setGuideOpen(false);
  }, []);

  const install = useCallback(async () => {
    if (deferred) {
      // Chromium: fire the native install prompt.
      try {
        await deferred.prompt();
        await deferred.userChoice;
      } catch {}
      setDeferred(null);
      setHidden(true);
      return;
    }
    // iOS / iPadOS Safari: no JS install API exists, so open the step-by-step
    // guide sheet — this is what "the button" now does on tablet / iPhone.
    setGuideOpen(true);
  }, [deferred]);

  if (hidden) return null;
  if (!deferred && !showIOS) return null;

  return (
    <>
      <div
        data-testid="pwa-install-prompt"
        style={{
          position: "fixed",
          left: "50%",
          transform: "translateX(-50%)",
          bottom: "calc(88px + env(safe-area-inset-bottom, 0px))",
          zIndex: 100,
          maxWidth: "min(92vw, 460px)",
          padding: "14px 18px",
          borderRadius: 24,
          background:
            "radial-gradient(120% 100% at 22% 0%, rgba(var(--role-green-rgb),0.20), rgba(var(--role-green-rgb),0.04) 62%, rgba(5,6,12,0.95) 100%)",
          boxShadow:
            "0 0 60px rgba(var(--role-green-rgb),0.28), 0 0 42px rgba(var(--role-green-rgb),0.14) inset",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          color: INK,
          fontFamily: YFONT,
          display: "flex",
          alignItems: "center",
          gap: 14,
          animation: "yuo-emerge .9s cubic-bezier(.2,.7,.2,1) both",
        }}
      >
        <StoneOrb color="growth" size={26} />
        <div style={{ flex: 1, minWidth: 0, lineHeight: 1.45 }}>
          <div style={{
            fontSize: 9.5, letterSpacing: "0.24em", textTransform: "uppercase",
            color: "rgba(166,255,207,0.65)", marginBottom: 3,
          }}>
            YUO · install
          </div>
          <div style={{ fontSize: 13, color: INK }}>
            Add YUO to your home screen for one-tap access.
          </div>
        </div>
        <button
          onClick={install}
          data-testid="pwa-install-btn"
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "8px 16px", borderRadius: 999,
            background: "radial-gradient(120% 160% at 30% 0%, rgba(var(--role-green-rgb),0.36), rgba(var(--role-green-rgb),0.08))",
            boxShadow: "0 0 22px rgba(var(--role-green-rgb),0.34)",
            border: "none", cursor: "pointer",
            color: INK, fontFamily: YFONT, fontSize: 10,
            letterSpacing: "0.22em", textTransform: "uppercase", whiteSpace: "nowrap",
          }}>
          Install
        </button>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="pwa-install-dismiss"
          style={{
            background: "transparent", border: "none", cursor: "pointer",
            color: MUTED_60, fontFamily: YFONT, fontSize: 18, padding: "0 4px",
            lineHeight: 1,
          }}>
          ×
        </button>
      </div>
      {guideOpen && <IOSInstallGuide onClose={() => setGuideOpen(false)} />}
    </>
  );
}

/**
 * IOSInstallGuide — Safari has no install API, so we walk the user through
 * the two-tap flow: Share (􀈂) → Add to Home Screen.
 */
function IOSInstallGuide({ onClose }) {
  return (
    <div
      onClick={onClose}
      data-testid="pwa-ios-guide"
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: "var(--dk-3410-60)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 22,
        animation: "yuo-emerge .55s cubic-bezier(.2,.7,.2,1) both",
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 460, width: "100%",
          padding: "26px 26px 22px",
          borderRadius: 32,
          background: "radial-gradient(120% 100% at 22% 0%, rgba(var(--role-green-rgb),0.20), rgba(var(--role-green-rgb),0.04) 62%, var(--dk-101220-65) 100%)",
          boxShadow: "0 0 70px rgba(var(--role-green-rgb),0.28)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          color: INK, fontFamily: YFONT,
          display: "flex", flexDirection: "column", gap: 16,
        }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <StoneOrb color="growth" size={22} />
          <div>
            <div style={{ fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase",
              color: "rgba(166,255,207,0.7)" }}>YUO · install</div>
            <div style={{ fontSize: 17, marginTop: 2 }}>Add YUO to your home screen</div>
          </div>
        </div>

        <Step n={1} accent="#34FF96">
          Tap the <b>Share</b> icon
          <span aria-hidden style={{
            display: "inline-flex", verticalAlign: "-3px", marginLeft: 6,
            width: 20, height: 20, borderRadius: 6,
            background: "rgba(var(--role-green-rgb),0.15)", color: "var(--lit-green-5)",
            alignItems: "center", justifyContent: "center", fontSize: 12,
          }}>􀈂</span> at the top-right of Safari.
        </Step>
        <Step n={2} accent="#34FF96">
          Scroll and tap <b>Add to Home Screen</b>.
        </Step>
        <Step n={3} accent="#34FF96">
          Tap <b>Add</b> — YUO appears with the stone icon on your home screen.
        </Step>

        <div style={{
          fontSize: 11, color: "var(--fg-5)", lineHeight: 1.6,
          padding: "8px 12px", borderRadius: 14,
          background: "var(--fg-04)",
        }}>
          On iPad / iPhone the browser doesn't allow apps to install themselves —
          this two-tap flow is Apple's built-in way.
        </div>

        <button
          onClick={onClose}
          data-testid="pwa-ios-guide-close"
          style={{
            alignSelf: "stretch", padding: "12px 22px",
            borderRadius: 999, border: "none", cursor: "pointer",
            background: "radial-gradient(120% 160% at 30% 0%, rgba(var(--role-green-rgb),0.34), rgba(var(--role-green-rgb),0.08))",
            boxShadow: "0 0 26px rgba(var(--role-green-rgb),0.32)",
            color: INK, fontFamily: YFONT, fontSize: 11,
            letterSpacing: "0.22em", textTransform: "uppercase",
          }}>
          Got it
        </button>
      </div>
    </div>
  );
}

function Step({ n, accent, children }) {
  return (
    <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
      <div style={{
        flex: "0 0 auto",
        width: 28, height: 28, borderRadius: "50%",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        background: `radial-gradient(38% 34% at 34% 26%, #fff, ${accent} 22%, #05060c 100%)`,
        boxShadow: `0 0 14px ${accent}`,
        color: "#0b1a12", fontFamily: YFONT, fontSize: 12, fontWeight: 700,
      }}>{n}</div>
      <div style={{ flex: 1, fontSize: 14, lineHeight: 1.6, color: INK, paddingTop: 3 }}>
        {children}
      </div>
    </div>
  );
}
